import { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Sidebar } from '@/components/Sidebar';
import { EmailAddress } from '@/components/EmailAddress';
import { EmailList } from '@/components/EmailList';
import { EmailViewer } from '@/components/EmailViewer';
import { useInbox } from '@/hooks/useInbox';
import { ParticleBackground } from '@/components/ParticleBackground';
import { AdBanner } from '@/components/AdBanner';
import { ResponsiveAdBanner } from '@/components/ResponsiveAdBanner';
import { NewYearBanner } from '@/components/NewYearBanner';
import { Mail, Sparkles } from 'lucide-react';

const Index = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    session,
    emails,
    selectedEmail,
    isLoading,
    timeRemaining,
    unreadCount,
    refreshInbox,
    generateNew,
    deleteEmail,
    selectEmail,
    sendTest,
  } = useInbox();

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-surface gradient-mesh relative overflow-hidden">
        <ParticleBackground />
        <div className="flex flex-col items-center gap-8 animate-fade-in relative z-10">
          <div className="relative">
            <div className="absolute -inset-4 rounded-full gradient-bg opacity-50 blur-2xl animate-pulse-slow" />
            <div className="w-24 h-24 rounded-3xl gradient-bg flex items-center justify-center shadow-glow-intense animate-float">
              <Mail className="w-12 h-12 text-primary-foreground" />
            </div>
            <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-accent flex items-center justify-center shadow-glow-accent animate-bounce-subtle">
              <Sparkles className="w-5 h-5 text-accent-foreground animate-wiggle" />
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-3 gradient-text">Initializing Secure Inbox</h1>
            <p className="text-muted-foreground text-lg">Setting up your private email...</p>
          </div>
          <div className="flex gap-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="w-3 h-3 rounded-full gradient-bg animate-bounce-subtle"
                style={{ animationDelay: `${i * 0.12}s` }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-surface gradient-mesh flex flex-col relative overflow-hidden">
      <NewYearBanner />
      <ParticleBackground />
      <Navbar 
        onMenuClick={() => setSidebarOpen(true)}
        rightContent={
          unreadCount > 0 ? (
            <div className="flex items-center gap-3 px-5 py-2.5 rounded-full bg-primary/15 border border-primary/30 hover-glow animate-fade-in">
              <div className="w-2.5 h-2.5 rounded-full gradient-bg animate-glow-pulse" />
              <span className="text-sm font-bold text-primary">{unreadCount} new</span>
            </div>
          ) : null
        }
      />

      <div className="flex-1 flex overflow-hidden relative z-10">
        <Sidebar
          unreadCount={unreadCount}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Email Address Section */}
          <div className="p-4 lg:p-6">
            <EmailAddress
              email={session.email}
              timeRemaining={timeRemaining}
              onRefresh={refreshInbox}
              onGenerateNew={generateNew}
              onSendTest={sendTest}
              isLoading={isLoading}
            />
          </div>

          {/* Email Content Area */}
          <div className="flex-1 flex overflow-hidden px-4 lg:px-6 pb-4 lg:pb-6">
            <div className="flex-1 glass-premium rounded-3xl overflow-hidden flex flex-col lg:flex-row glow-border shadow-glow-intense">
              {/* Email List */}
              <div
                className={`w-full lg:w-96 border-r border-border/40 flex flex-col ${
                  selectedEmail ? 'hidden lg:flex' : 'flex'
                }`}
              >
                <div className="p-5 border-b border-border/40">
                  <h1 className="text-lg font-semibold">Inbox</h1>
                  <p className="text-sm text-muted-foreground">
                    {emails.length} {emails.length === 1 ? 'message' : 'messages'}
                  </p>
                </div>
                <EmailList
                  emails={emails}
                  selectedEmail={selectedEmail}
                  onSelectEmail={selectEmail}
                  isLoading={isLoading}
                />
              </div>

              {/* Ad Banner - Between List and Viewer */}
              <div className="flex shrink-0 items-center justify-center border-r border-border/40 px-2 py-3 bg-secondary/20">
                <AdBanner />
              </div>

              {/* Email Viewer */}
              <div className={`flex-1 ${selectedEmail ? 'flex' : 'hidden lg:flex'} flex-col`}>
                {selectedEmail ? (
                  <EmailViewer
                    email={selectedEmail}
                    onBack={() => selectEmail(null)}
                    onDelete={deleteEmail}
                  />
                ) : (
                  <div className="flex-1 flex items-center justify-center p-8">
                    <div className="text-center max-w-sm animate-fade-in">
                      <div className="relative inline-block mb-8">
                        <div className="absolute -inset-4 rounded-full bg-secondary/50 blur-xl" />
                        <div className="relative w-24 h-24 rounded-3xl bg-secondary flex items-center justify-center animate-float">
                          <Mail className="w-12 h-12 text-muted-foreground" />
                        </div>
                        <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full gradient-bg flex items-center justify-center shadow-glow animate-bounce-subtle">
                          <Sparkles className="w-4 h-4 text-primary-foreground animate-wiggle" />
                        </div>
                      </div>
                      <h2 className="text-2xl font-bold mb-3 gradient-text">Select an email</h2>
                      <p className="text-muted-foreground leading-relaxed text-lg">
                        Choose a message from your inbox to view its contents
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Ad Banners */}
          <div className="px-4 lg:px-6 pb-4 space-y-3">
            <div className="glass rounded-2xl p-2 flex justify-center">
              <AdBanner />
            </div>
            <div className="glass rounded-2xl p-2">
              <ResponsiveAdBanner variant="stacked" />
            </div>
          </div>

          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Index;
